import { Component, EventEmitter, Output, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { ChatService } from '../../../../../services/chat.service';
import { ChatMessage } from '../../../../../models/chat-message.model';
import { AuthService } from '../../../../../services/auth.service';
import { environment } from '../../../../../../environments/environment';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FlagService } from '../../../../../shared/services/flag.service';
import { FlagType } from '../../../../../shared/utils/enums';

@Component({
    selector: 'app-chat-popup',
    templateUrl: './chat-popup.component.html',
    styleUrls: ['./chat-popup.component.scss']
})
export class ChatPopupComponent implements OnInit, OnDestroy, AfterViewChecked {
    @Output() closeChat = new EventEmitter<void>();
    @ViewChild('messageContainer') private messageContainer!: ElementRef;
    @ViewChild('fileInput') private fileInput!: ElementRef;
    @ViewChild('textareaInput') private textareaInput!: ElementRef;

    messages: ChatMessage[] = [];
    newMessage: string = '';
    currentUserId: number | null = null;
    candidatId: number | null = null;
    agencyId: number | null = null;
    private shouldScrollToBottom = false;
    private globalMessagesSubscription: Subscription | null = null; // Global subscription for sound alerts
    private unreadCountSubscription: Subscription | null = null;
    private conversationOpen = true; // Track if conversation is currently open

    // UI State
    isExpanded = false;

    // Voice recording
    isRecording = false;
    mediaRecorder: MediaRecorder | null = null;
    audioChunks: Blob[] = [];
    recordingDuration = 0;
    private recordingInterval: any;

    // File attachment
    selectedFile: File | null = null;
    readonly maxFileSizeMo = 5;
    private closePopupSubscription: Subscription | null = null;

    constructor(
        private chatService: ChatService,
        private authService: AuthService,
        private router: Router,
        private flag: FlagService
    ) { }

    ngOnInit(): void {
        // Get current user info
        const user = this.authService.user;
        if (user) {
            this.currentUserId = user.collary_id || null;
            this.candidatId = user.collary_id || null;
            this.agencyId = user.agency_id || null;

            // Set current user ID in chat service for sound filtering
            if (this.currentUserId) {
                this.chatService.setCurrentUserId(this.currentUserId);
            }

            if (this.candidatId && this.agencyId) {
                // Mark conversation as opened IMMEDIATELY
                this.conversationOpen = true;

                // Notify that chat is opened
                this.chatService.notifyChatOpen();

                this.loadConversationHistory();

                this.chatService.connect(this.candidatId, this.agencyId);

                // Notify server that conversation is opened - triggers auto-read via WebSocket
                this.chatService.notifyConversationOpen(
                    this.candidatId,
                    this.agencyId,
                    this.currentUserId!!,
                    'CANDIDAT'
                );

                // ALSO mark messages as read via REST API for immediate database update
                this.chatService.markMessagesAsRead(this.candidatId, this.agencyId, this.currentUserId!!).subscribe({
                    next: () => {
                        console.log('Messages marked as read via REST API');
                    },
                    error: (error) => {
                        console.error('Error marking messages as read:', error);
                    }
                });

                // Subscribe to sound notifications from chat service
                this.globalMessagesSubscription = this.chatService.soundNotification$.subscribe(() => {
                    this.chatService.playMessageSound();
                });

                // Subscribe to incoming messages
                this.chatService.messages$.subscribe((message: ChatMessage) => {
                    // If conversation is open, mark incoming messages as read immediately
                    if (this.conversationOpen) {
                        message.isRead = true;
                        // Also mark in database immediately via WebSocket notification
                        this.chatService.notifyConversationOpen(
                            this.candidatId!!,
                            this.agencyId!!,
                            this.currentUserId!!,
                            'CANDIDAT'
                        );
                    }
                    this.messages.push(message);
                    this.shouldScrollToBottom = true;
                });

                // Subscribe to unread count updates - only process if conversation is NOT open
                this.unreadCountSubscription = this.chatService.unreadCount$.subscribe((update) => {
                    // If conversation is open, ignore unread count updates for this conversation
                    if (!this.conversationOpen) {
                        console.log('Unread count updated:', update);
                    }
                });

                // Subscribe to close popup event from message dialog
                this.closePopupSubscription = this.chatService.closePopup$.subscribe(() => {
                    this.closeChat.emit();
                });
            }
        }
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
    }

    ngOnDestroy(): void {
        // Mark conversation as closed so incoming messages count as unread
        this.conversationOpen = false;

        // Notify that chat is closed
        this.chatService.notifyChatClose();

        if (this.unreadCountSubscription) {
            this.unreadCountSubscription.unsubscribe();
        }
        if (this.closePopupSubscription) {
            this.closePopupSubscription.unsubscribe();
        }
        if (this.globalMessagesSubscription) {
            this.globalMessagesSubscription.unsubscribe();
        }
        this.chatService.disconnect();
        if (this.recordingInterval) {
            clearInterval(this.recordingInterval);
        }
    }

    toggleExpand(): void {
        this.isExpanded = !this.isExpanded;
    }

    loadConversationHistory(): void {
        if (this.candidatId && this.agencyId) {
            this.chatService.getConversationHistory(this.candidatId, this.agencyId).subscribe({
                next: (messages) => {
                    this.messages = messages.map(msg => ({
                        ...msg,
                        timestamp: new Date(msg.timestamp)
                    }));
                    // Ensure scroll happens after DOM updates
                    setTimeout(() => {
                        this.shouldScrollToBottom = true;
                    }, 100);
                },
                error: (error) => {
                    console.error('Error loading conversation history:', error);
                }
            });
        }
    }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'video/mp4', 'video/webm', 'video/ogg'];
      if (!validTypes.includes(file.type)) {
        this.flag.displayFlag('Seuls les fichiers PNG, JPEG, JPG, PDF et vidéos sont autorisés.', FlagType.error);
        this.fileInput.nativeElement.value = '';
        return;
      }
      // Validate file size (max maxFileSizeMo Mo)
      if (file.size > this.maxFileSizeMo * 1024 * 1024) {
        this.flag.displayFlag(`La taille du fichier ne doit pas dépasser ${this.maxFileSizeMo} Mo.`, FlagType.error);
        this.fileInput.nativeElement.value = '';
        return;
      }

            this.selectedFile = file;
            this.sendFileAttachment();
        }
    }

    triggerFileInput(): void {
        this.fileInput.nativeElement.click();
    }

    sendFileAttachment(): void {
        if (!this.selectedFile || !this.currentUserId || !this.candidatId || !this.agencyId) {
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const base64Data = (reader.result as string).split(',')[1];
            const user = this.authService.user;
            const senderName = user?.email ? user.email : 'Candidat';

      let attachmentType: 'IMAGE' | 'PDF' | 'VIDEO' = 'IMAGE';
      if (this.selectedFile!.type === 'application/pdf') {
        attachmentType = 'PDF';
      } else if (this.selectedFile!.type.startsWith('video/')) {
        attachmentType = 'VIDEO';
      }            const message: any = {
                senderId: this.currentUserId,
                senderType: 'CANDIDAT',
                senderName: senderName,
                candidatId: this.candidatId,
                agencyId: this.agencyId,
                message: '',
                timestamp: new Date(),
                isRead: false,
                attachmentType: attachmentType,
                attachmentPath: base64Data, // Send base64 in path field for WebSocket
                attachmentFileName: this.selectedFile!.name
            };

            this.chatService.sendMessage(message);
            this.selectedFile = null;
            this.fileInput.nativeElement.value = '';
        };

        reader.readAsDataURL(this.selectedFile);
    }

    async startRecording(): Promise<void> {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.mediaRecorder = new MediaRecorder(stream);
            this.audioChunks = [];
            this.recordingDuration = 0;

            // Start recording timer
            this.recordingInterval = setInterval(() => {
                this.recordingDuration++;
            }, 1000);

            this.mediaRecorder.ondataavailable = (event) => {
                this.audioChunks.push(event.data);
            };

            this.mediaRecorder.onstop = () => {
                const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
                this.sendVoiceMessage(audioBlob);
                stream.getTracks().forEach(track => track.stop());

                // Clear timer
                if (this.recordingInterval) {
                    clearInterval(this.recordingInterval);
                    this.recordingInterval = null;
                }
            };

            this.mediaRecorder.start();
            this.isRecording = true;
        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('Impossible d\'accéder au microphone');
        }
    }

    stopRecording(): void {
        if (this.mediaRecorder && this.isRecording) {
            this.mediaRecorder.stop();
            this.isRecording = false;
        }
    }

    sendVoiceMessage(audioBlob: Blob): void {
        if (!this.currentUserId || !this.candidatId || !this.agencyId) {
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const base64Data = (reader.result as string).split(',')[1];
            const user = this.authService.user;
            const senderName = user?.email ? user.email : 'Candidat';

            const message: any = {
                senderId: this.currentUserId,
                senderType: 'CANDIDAT',
                senderName: senderName,
                candidatId: this.candidatId,
                agencyId: this.agencyId,
                message: '',
                timestamp: new Date(),
                isRead: false,
                attachmentType: 'VOICE',
                attachmentPath: base64Data, // Send base64 in path field for WebSocket
                attachmentFileName: `voice_${Date.now()}.webm`
            };

            this.chatService.sendMessage(message);
        };

        reader.readAsDataURL(audioBlob);
    }

    sendMessage(): void {
        if (!this.newMessage.trim() || !this.currentUserId || !this.candidatId || !this.agencyId) {
            return;
        }

        const user = this.authService.user;
        const senderName = user?.email
            ? user.email
            : 'Candidat';

        const message: ChatMessage = {
            senderId: this.currentUserId,
            senderType: 'CANDIDAT',
            senderName: senderName,
            candidatId: this.candidatId,
            agencyId: this.agencyId,
            message: this.newMessage.trim(),
            timestamp: new Date(),
            isRead: false
        };

        this.chatService.sendMessage(message);
        this.newMessage = '';
        this.resetTextareaHeight();
    }

    onKeyPress(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        } else {
            // Auto-expand textarea on input
            setTimeout(() => this.autoExpandTextarea(), 0);
        }
    }

    autoExpandTextarea(): void {
        if (this.textareaInput && this.textareaInput.nativeElement) {
            const textarea = this.textareaInput.nativeElement;
            textarea.style.height = 'auto';
            const newHeight = Math.min(textarea.scrollHeight, 192); // 8 lines max (24px line-height * 8 = 192px)
            textarea.style.height = newHeight + 'px';
        }
    }

    resetTextareaHeight(): void {
        if (this.textareaInput && this.textareaInput.nativeElement) {
            const textarea = this.textareaInput.nativeElement;
            textarea.style.height = 'unset';
        }
    }

    isMyMessage(message: ChatMessage): boolean {
        return message.senderId === this.currentUserId;
    }

    getAttachmentUrl(message: ChatMessage): string {
        return `${environment.api.BASE_URL_NOTIF}${message.attachmentPath}`;
    }

    toggleVoicePlay(message: any): void {
        const audioElements = document.querySelectorAll('audio');
        audioElements.forEach((audio: HTMLAudioElement) => {
            if (audio.src === this.getAttachmentUrl(message)) {
                if (audio.paused) {
                    // Pause all other audio
                    audioElements.forEach(a => {
                        if (a !== audio) {
                            a.pause();
                            a.currentTime = 0;
                        }
                    });

                    // Update all messages to not playing
                    this.messages.forEach(m => (m as any).isPlaying = false);

                    // Play this audio
                    audio.play();
                    (message as any).isPlaying = true;
                } else {
                    audio.pause();
                    (message as any).isPlaying = false;
                }
            }
        });
    }

    onVoiceEnded(message: any): void {
        (message as any).isPlaying = false;
    }

    openMissionDetails(message: ChatMessage): void {
        if (message.missionId) {
            window.open(`/missinDetails/${message.missionId}`, '_blank');
        }
    }

    private scrollToBottom(): void {
        try {
            if (this.messageContainer) {
                this.messageContainer.nativeElement.scrollTop = this.messageContainer.nativeElement.scrollHeight;
            }
        } catch (err) {
            console.error('Error scrolling to bottom:', err);
        }
    }
}